import { useEffect, useState } from "react";

/** Cadre tel qu’il était conçu en grand écran (`max-width: 1300px`). */
export const APP_FRAME_WIDTH = 1300;
export const APP_FRAME_HEIGHT = 860;
/** Débord des arabesques, pour ne pas les rogner au zoom. */
const ORNAMENT_BLEED = 40;
/** Air en haut et en bas de la fenêtre. */
const VERTICAL_VIEW_GAP = 32;
/** Air restant à gauche et à droite une fois le cadre élargi. */
const HORIZONTAL_VIEW_GAP = 32;
/** Élargissement du cadre (px design), sans changer la hauteur. */
const EXTRA_WIDTH = 180;

function getViewportSize() {
  return {
    width: window.visualViewport?.width || window.innerWidth,
    height: window.visualViewport?.height || window.innerHeight,
  };
}

function safeNumber(value, fallback) {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function computeFit(frameGrowY = 0, frameGrowX = frameGrowY) {
  const growY = frameGrowY > 0 ? frameGrowY : 0;
  const growX = frameGrowX > 0 ? frameGrowX : 0;
  const { width, height } = getViewportSize();
  const naturalWidth = Math.min(width * 0.8, APP_FRAME_WIDTH);
  const naturalHeight = Math.min(height * 0.8, height - 200);
  const baseInnerWidth = APP_FRAME_WIDTH + ORNAMENT_BLEED * 2;
  const innerHeight = APP_FRAME_HEIGHT + ORNAMENT_BLEED * 2;

  // Assez de place : on garde l’ancienne mise en page (1300 × 80vh).
  // L’agrandissement du mode 125 % est ajouté ensuite, échelle 1.
  if (naturalWidth >= APP_FRAME_WIDTH && naturalHeight >= 800) {
    return {
      scaled: false,
      scale: 1,
      frameWidth: APP_FRAME_WIDTH,
      frameHeight: APP_FRAME_HEIGHT,
      innerWidth: APP_FRAME_WIDTH,
      innerHeight: APP_FRAME_HEIGHT,
      slotWidth: APP_FRAME_WIDTH,
      slotHeight: APP_FRAME_HEIGHT,
    };
  }

  const scaleY = (height - VERTICAL_VIEW_GAP * 2) / innerHeight;
  const maxInnerAtScaleY = (width - HORIZONTAL_VIEW_GAP * 2) / scaleY;

  let scale;
  let innerWidth;

  if (maxInnerAtScaleY >= baseInnerWidth) {
    scale = safeNumber(scaleY, 1);
    innerWidth = Math.min(baseInnerWidth + EXTRA_WIDTH, maxInnerAtScaleY);
  } else {
    innerWidth = baseInnerWidth;
    scale = safeNumber((width - HORIZONTAL_VIEW_GAP * 2) / innerWidth, 1);
  }

  const frameWidth = innerWidth - ORNAMENT_BLEED * 2;
  // Pixels écran, pas pixels du dessin : sinon le scale recalculé les annule
  // et le cadre ne bouge pas.
  const designGrowX = growX > 0 && scale > 0 ? growX / scale : 0;
  const designGrowY = growY > 0 && scale > 0 ? growY / scale : 0;

  return {
    scaled: true,
    scale,
    frameWidth: frameWidth + designGrowX,
    frameHeight: APP_FRAME_HEIGHT + designGrowY,
    innerWidth: innerWidth + designGrowX,
    innerHeight: innerHeight + designGrowY,
    slotWidth: (innerWidth + designGrowX) * scale,
    slotHeight: (innerHeight + designGrowY) * scale,
  };
}

const INITIAL_FIT = {
  scaled: false,
  scale: 1,
  frameWidth: APP_FRAME_WIDTH,
  frameHeight: APP_FRAME_HEIGHT,
  innerWidth: APP_FRAME_WIDTH,
  innerHeight: APP_FRAME_HEIGHT,
  slotWidth: APP_FRAME_WIDTH,
  slotHeight: APP_FRAME_HEIGHT,
};

export default function useAppFitScale(frameGrowY = 0, frameGrowX = frameGrowY) {
  const [fit, setFit] = useState(() =>
    typeof window === "undefined" ? INITIAL_FIT : computeFit(frameGrowY, frameGrowX)
  );

  useEffect(() => {
    const update = () => {
      const next = computeFit(frameGrowY, frameGrowX);
      setFit((prev) => {
        if (
          prev.scaled === next.scaled &&
          Math.abs(prev.scale - next.scale) < 0.001 &&
          Math.abs(prev.innerWidth - next.innerWidth) < 0.5 &&
          Math.abs(prev.innerHeight - next.innerHeight) < 0.5 &&
          Math.abs(prev.frameWidth - next.frameWidth) < 0.5 &&
          Math.abs(prev.frameHeight - next.frameHeight) < 0.5
        ) {
          return prev;
        }
        return next;
      });
    };

    update();
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);

    return () => {
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, [frameGrowY, frameGrowX]);

  useEffect(() => {
    document.documentElement.style.setProperty("--app-scale", String(fit.scale));
    return () => {
      document.documentElement.style.removeProperty("--app-scale");
    };
  }, [fit.scale]);

  return fit;
}
